import React from "react"
import { View, StyleSheet, TouchableOpacity, Image } from "react-native"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"

/**
 * Grille de pastilles (chips) compactes pour les listes d'options longues du
 * funnel — typiquement les classes (CE2…Terminale + Autre) ou les matières
 * prioritaires, trop nombreuses pour des cartes-réponses pleine largeur
 * (AnswerCardM) qui rendraient l'écran interminable.
 *
 * Cohérent visuellement avec le reste du funnel : mêmes couleurs de thème, même
 * rayon (~14, aligné sur AnswerCardM / BrevetChip), même état sélectionné
 * (bordure `primary` + fond `primaryLighter`, label en gras `primary`), espacement
 * aéré. Le composant ne fait que rendre les chips et remonter `onToggle(id)` — la
 * SÉMANTIQUE est au choix de l'appelant : mono-sélection (radio : `selectedIds` à
 * 0/1 élément, ex. la classe) OU multi-sélection (`selectedIds` à N éléments, ex.
 * les matières prioritaires plafonnées). Pour le multi avec plafond, l'appelant
 * passe `disabledIds` pour griser les options non encore sélectionnées une fois le
 * plafond atteint.
 *
 * Icône OPTIONNELLE par option (`opt.iconUri`) : si présente, une petite image est
 * rendue à GAUCHE du label dans la pastille (ex. icônes de matières renvoyées par
 * l'API). Absente → pastille label-seul inchangée (ex. l'étape classe).
 *
 * Pastilles à largeur intrinsèque qui se renvoient à la ligne (flexWrap), donc
 * compactes mais respirantes.
 */
const ClassChipGridM = ({
    options = [],
    selectedIds = [],
    disabledIds = [],
    onToggle,
    style,
}) => {
    return (
        <View style={[styles.grid, style]}>
            {options.map((opt) => {
                const selected = selectedIds.includes(opt.id)
                const disabled = !selected && disabledIds.includes(opt.id)
                return (
                    <TouchableOpacity
                        key={opt.id}
                        activeOpacity={0.85}
                        disabled={disabled}
                        onPress={() => onToggle?.(opt.id)}
                        style={[
                            styles.chip,
                            selected && styles.chipSelected,
                            disabled && styles.chipDisabled,
                        ]}
                    >
                        {opt.iconUri ? (
                            <Image
                                source={{ uri: opt.iconUri }}
                                style={[
                                    styles.icon,
                                    disabled && styles.iconDisabled,
                                ]}
                                resizeMode="contain"
                            />
                        ) : null}
                        <TextM
                            fontWeight={selected ? "Bold" : "SemiBold"}
                            fontSize={14}
                            color={
                                selected
                                    ? theme.colors.primary
                                    : disabled
                                      ? theme.colors.lightMore
                                      : theme.colors.grey
                            }
                        >
                            {opt.label}
                        </TextM>
                    </TouchableOpacity>
                )
            })}
        </View>
    )
}

const styles = StyleSheet.create({
    grid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 10,
    },
    chip: {
        minWidth: 64,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        borderRadius: 14,
        paddingVertical: 12,
        paddingHorizontal: 16,
        backgroundColor: theme.colors.white,
        // Même relief discret que les cartes-réponses du funnel.
        shadowColor: "#000",
        shadowOpacity: 0.04,
        shadowRadius: 6,
        shadowOffset: { width: 0, height: 2 },
    },
    chipSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    chipDisabled: {
        opacity: 0.5,
        backgroundColor: theme.colors.whiteAlmost,
    },
    // Petite icône-image à gauche du label (icône de matière). Compacte pour
    // garder la pastille dense (alignée sur l'icône d'AnswerCardM, 24px).
    icon: {
        width: 18,
        height: 18,
        marginRight: 8,
    },
    iconDisabled: { opacity: 0.6 },
})

export default ClassChipGridM
